import React, { useState, useEffect } from 'react';
import {
  X, Eye, Image as ImageIcon, Network, Phone, Mail,
  ChevronLeft, ChevronRight, Edit2
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Modal de ficha de una incidencia: datos, imágenes adjuntas y lightbox.
 *
 * Abre INSTANTÁNEO con el objeto que ya tiene el listado y se enriquece con la
 * ficha completa que devuelve `detalleUrl` (el listado NO trae `adjuntos`, solo
 * `adjuntos_count`). Si ese fetch falla, el modal sigue siendo usable con los
 * datos del listado — nunca se cierra solo ni se queda en blanco.
 *
 * Props:
 *   - incidencia             objeto del listado. `null` => no se renderiza nada.
 *   - detalleUrl             (id) => string. URL absoluta de la ficha completa.
 *
 *       OJO, ACÁ ESTÁ LA TRAMPA: en el backend conviven DOS rutas de detalle.
 *       `GET /api/incidencias/:id`      → verifyToken + soloAdmin  → 403 al cliente
 *       `GET /api/incidencias/mias/:id` → verifyToken, scoped a la red de la clínica
 *       Por eso la ruta viaja por prop y no hardcodeada: un intento anterior de
 *       llevar esta ficha al portal de cliente reusó la de admin y comía un 403.
 *
 *   - onClose()              cierra el modal.
 *   - mostrarCamposInternos  default false. true => añade carga de trabajo y
 *                            responsable (datos internos de gestión).
 *   - onEditar(incidencia)   opcional. Si no se pasa, no se pinta el botón Editar.
 */

// Los cuatro estados del tablero. Se replican los colores de INC_COLS/EST_COL de
// ClientManagementCli para que la ficha y las columnas hablen el mismo idioma.
const ESTADOS = {
  abierta:    { label: 'Abierta',    color: '#0ea5e9' },
  en_proceso: { label: 'En proceso', color: '#f59e0b' },
  resuelta:   { label: 'Resuelta',   color: '#10b981' },
  cerrada:    { label: 'Cerrada',    color: '#9ca3af' },
};

const PRIORIDADES = {
  critica: { label: 'Crítica', cls: 'bg-red-50 text-red-700' },
  alta:    { label: 'Alta',    cls: 'bg-amber-50 text-amber-700' },
  media:   { label: 'Media',   cls: 'bg-blue-50 text-blue-700' },
  baja:    { label: 'Baja',    cls: 'bg-gray-100 text-gray-600' },
};

/* Los adjuntos llegan con url relativa (/uploads/...) y los sirve routes/Archivos.js
   SIN auth, para que <img src> funcione directo. Si el API vive en otro origen hay
   que anteponerlo; si ya es absoluta, se deja tal cual. */
const archivoSrc = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

const fechaCorta = (v) => (v ? new Date(v).toLocaleDateString('es-ES') : '—');

export default function IncidenciaDetalleModal({
  incidencia,
  detalleUrl,
  onClose,
  mostrarCamposInternos = false,
  onEditar,
}) {
  const { theme } = useTheme();

  const [detalle,        setDetalle]        = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleError,   setDetalleError]   = useState('');
  const [lightboxOpen,   setLightboxOpen]   = useState(false);
  const [lightboxIndex,  setLightboxIndex]  = useState(0);

  const incidenciaId = incidencia?.id;

  // Ficha completa. `cancelado` evita el setState sobre un modal ya cerrado.
  useEffect(() => {
    if (!incidenciaId) { setDetalle(null); setDetalleError(''); setLightboxOpen(false); return; }
    let cancelado = false;
    (async () => {
      setDetalleLoading(true);
      setDetalleError('');
      try {
        const res = await fetch(detalleUrl(incidenciaId), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          // 404 = la incidencia no pertenece a la red del usuario (el backend no
          // distingue "no existe" de "no es tuya", a propósito).
          throw new Error(res.status === 404
            ? 'Esta incidencia no está disponible.'
            : 'No se pudo cargar la ficha completa.');
        }
        const data = await res.json();
        if (!cancelado) setDetalle(data.incidencia || null);
      } catch (e) {
        if (!cancelado) setDetalleError(e.message || 'No se pudo cargar la ficha completa.');
      } finally {
        if (!cancelado) setDetalleLoading(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidenciaId]);

  // Fuente de verdad: la ficha completa si ya llegó, si no lo del listado.
  const D = detalle || incidencia || {};
  const detalleImgs = (detalle?.adjuntos || []).filter(a => a.es_imagen);

  // ── Lightbox ────────────────────────────────────────────────────────────
  const openLightbox  = (index) => { setLightboxIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => setLightboxOpen(false);
  const navigateLightbox = (dir) => {
    setLightboxIndex(prev => {
      const next = prev + dir;
      if (next < 0) return detalleImgs.length - 1;
      if (next >= detalleImgs.length) return 0;
      return next;
    });
  };

  // Un solo handler de teclado, con prioridad: si el lightbox está abierto,
  // Escape lo cierra a él y NO llega al modal. Si están los dos escuchando por
  // separado, una sola pulsación cierra las dos capas de golpe.
  useEffect(() => {
    if (!incidenciaId) return undefined;
    const handleKey = (e) => {
      if (lightboxOpen) {
        if (e.key === 'Escape')     { closeLightbox(); return; }
        if (e.key === 'ArrowLeft')  navigateLightbox(-1);
        if (e.key === 'ArrowRight') navigateLightbox(1);
        return;
      }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidenciaId, lightboxOpen, detalleImgs.length]);

  if (!incidencia) return null;

  const bgPrimary = { backgroundColor: theme.primary };
  const est  = ESTADOS[D.estado] || { label: D.estado || 'Sin estado', color: '#9ca3af' };
  const prio = PRIORIDADES[D.prioridad] || { label: D.prioridad || '—', cls: 'bg-gray-100 text-gray-600' };

  const metadatos = [
    ['Centro',            D.cliente_nombre || '—'],
    ['Tipo',              D.tipo_incidencia || '—'],
    ['Fecha de alta',     fechaCorta(D.fecha)],
    ['Fecha resolución',  fechaCorta(D.fecha_resolucion)],
  ];
  if (mostrarCamposInternos) {
    metadatos.push(['Carga de trabajo', D.carga_trabajo != null ? `${parseFloat(D.carga_trabajo).toFixed(2)}h` : '—']);
    metadatos.push(['Responsable',      D.usuario_nombre || '—']);
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden">

          <div className="text-white px-6 py-4 flex items-center justify-between" style={bgPrimary}>
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-white/20 rounded-lg"><Eye size={15}/></div>
              <div>
                <h2 className="text-sm font-bold">Detalle de Incidencia</h2>
                <p className="opacity-75" style={{ fontSize:'10px' }}>#{D.id} · {D.cliente_nombre || '—'}</p>
              </div>
            </div>
            <button onClick={onClose} title="Cerrar (Esc)" className="p-1.5 rounded-lg hover:bg-white/20"><X size={15}/></button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">

            {/* Mientras llega la ficha completa se pinta lo del listado y se avisa
                con una barrita; nada de pantalla en blanco. */}
            {detalleLoading && !detalle && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-200"
                  style={{ borderTopColor: theme.primary }}/>
                Cargando ficha completa…
              </div>
            )}
            {detalleError && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {detalleError}
              </div>
            )}

            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-bold text-gray-900 flex-1">{D.nombre}</h3>
              <div className="flex gap-1.5 flex-shrink-0">
                <span className={`px-2 py-0.5 rounded-md font-semibold ${prio.cls}`} style={{ fontSize:'10px' }}>
                  {prio.label}
                </span>
                <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-md">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: est.color }}/>
                  <span className="text-gray-600 font-semibold" style={{ fontSize:'10px' }}>{est.label}</span>
                </div>
              </div>
            </div>

            {/* La posición en la red solo la trae la ficha completa. */}
            {D.matriz_nombre && (
              <div className="rounded-lg p-3 border border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2 mb-1.5">
                  <Network size={12} className="text-gray-400 flex-shrink-0"/>
                  <p className="font-bold text-gray-400 uppercase tracking-wider" style={{ fontSize:'9px' }}>Sede</p>
                </div>
                <p className="text-gray-500" style={{ fontSize:'11px' }}>
                  Pertenece a la clínica matriz <span className="font-semibold text-gray-700">{D.matriz_nombre}</span>
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              {metadatos.map(([label, val]) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3">
                  <p className="font-bold text-gray-400 uppercase tracking-wider mb-0.5" style={{ fontSize:'9px' }}>{label}</p>
                  <p className="font-semibold text-gray-800 text-xs break-words">{val}</p>
                </div>
              ))}
            </div>

            {(D.cliente_telefono || D.cliente_email) && (
              <div className="flex flex-wrap gap-3">
                {D.cliente_telefono && (
                  <a href={`tel:${D.cliente_telefono}`}
                    className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <Phone size={12} className="text-gray-400"/> {D.cliente_telefono}
                  </a>
                )}
                {D.cliente_email && (
                  <a href={`mailto:${D.cliente_email}`}
                    className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 break-all">
                    <Mail size={12} className="text-gray-400"/> {D.cliente_email}
                  </a>
                )}
              </div>
            )}

            {D.descripcion && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-bold text-gray-400 uppercase tracking-wider mb-1.5" style={{ fontSize:'9px' }}>Descripción</p>
                <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{D.descripcion}</p>
              </div>
            )}

            {D.resolucion && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-bold text-green-700 uppercase tracking-wider mb-1.5" style={{ fontSize:'9px' }}>Notas de resolución</p>
                <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">{D.resolucion}</p>
              </div>
            )}

            {detalleImgs.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon size={12} className="text-gray-400"/>
                  <p className="font-bold text-gray-400 uppercase tracking-wider" style={{ fontSize:'9px' }}>
                    Imágenes adjuntas ({detalleImgs.length})
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {detalleImgs.map((img, idx) => (
                    <button key={img.id} type="button" onClick={() => openLightbox(idx)}
                      title={img.nombre_original}
                      className="rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition-colors"
                      style={{ lineHeight: 0 }}>
                      <img src={archivoSrc(img.url)} alt={img.nombre_original}
                        style={{ width:'104px', height:'104px', objectFit:'cover', display:'block' }}/>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-2 bg-gray-50/50">
            {onEditar && (
              <button onClick={() => { onEditar(D); onClose(); }}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 text-sm font-semibold flex items-center gap-1.5 shadow-sm" style={bgPrimary}>
                <Edit2 size={12}/> Editar
              </button>
            )}
            <button onClick={onClose}
              className="px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-100 text-sm font-medium">
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox — por encima del modal (z 60). Colores y z-index inline: no
          dependen de utilidades Tailwind. */}
      {lightboxOpen && detalleImgs.length > 0 && (
        <div onClick={closeLightbox}
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ backgroundColor:'rgba(0,0,0,.92)', zIndex:60 }}>
          {/* Parar la propagación: si no, clicar la imagen cierra el lightbox */}
          <div onClick={e => e.stopPropagation()}
            className="relative flex flex-col items-center"
            style={{ maxWidth:'92vw', maxHeight:'92vh' }}>

            <button onClick={closeLightbox} title="Cerrar (Esc)"
              className="absolute text-white rounded-full flex items-center justify-center hover:opacity-100"
              style={{ top:'-6px', right:'-6px', width:'34px', height:'34px',
                       backgroundColor:'rgba(255,255,255,.15)', opacity:.85 }}>
              <X size={20}/>
            </button>

            {detalleImgs.length > 1 && (
              <>
                <button onClick={() => navigateLightbox(-1)} title="Anterior (←)"
                  className="absolute text-white rounded-full flex items-center justify-center"
                  style={{ left:'-52px', top:'50%', marginTop:'-22px', width:'44px', height:'44px',
                           backgroundColor:'rgba(255,255,255,.15)' }}>
                  <ChevronLeft size={30}/>
                </button>
                <button onClick={() => navigateLightbox(1)} title="Siguiente (→)"
                  className="absolute text-white rounded-full flex items-center justify-center"
                  style={{ right:'-52px', top:'50%', marginTop:'-22px', width:'44px', height:'44px',
                           backgroundColor:'rgba(255,255,255,.15)' }}>
                  <ChevronRight size={30}/>
                </button>
              </>
            )}

            <img
              src={archivoSrc(detalleImgs[lightboxIndex]?.url)}
              alt={detalleImgs[lightboxIndex]?.nombre_original || ''}
              style={{ maxWidth:'92vw', maxHeight:'80vh', objectFit:'contain', display:'block',
                       borderRadius:'8px', backgroundColor:'#111827' }}/>

            <div className="text-white text-center mt-3 flex items-center gap-3">
              <span className="text-xs truncate" style={{ maxWidth:'60vw', opacity:.85 }}>
                {detalleImgs[lightboxIndex]?.nombre_original}
              </span>
              <span className="text-xs font-semibold" style={{ opacity:.6 }}>
                {lightboxIndex + 1} / {detalleImgs.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
